<template>
    <div class="property-search-input w-100">
        <slot
                name="before"
        />

        <b-input-group class="flex-nowrap">
            <b-input-group-prepend>
              <span class="input-group-text-coherent search-icon">
                <i class="far fa-search" />
              </span>
            </b-input-group-prepend>
            <b-form-input
                    class="search-input"
                    :placeholder="placeholder"
                    :value="searchValue"
                    @update="handleSearch"
                    @focus="handleFocus"
                    @keyup.down="handleArrowDown"
                    @keyup.up="handleArrowUp"
                    @keyup.enter="handleEnter"
            />
        </b-input-group>
        <div
                class="results-wrapper"
                v-if="dropdownState"
        >
            <ul class="dropdown-menu">
                <b-dropdown-item v-if="loading">
                    <element-loader
                            :loading="true"
                            :height="30"
                    />
                </b-dropdown-item>

                <li
                        v-else-if="searchValue && !collection.length && !this.allowUserValue"
                        :disabled="false"
                        class="dropdown-item"
                >
                    Search by <em>{{ searchValue }}</em> has no results!
                </li>
                <b-dropdown-item
                        v-for="(resource, index) of collection"
                        :key="resource.key"
                        @click="() => setResource(resource)"
                        :link-class="{
                          'active': activeItem === index
                        }"
                >
                    {{ resource.name }}

                    <span class="float-right text-gray-600">
                        {{ resource.typeLabel }}
                    </span>
                </b-dropdown-item>
                <b-dropdown-item
                        v-if="searchValue && !loading && this.allowUserValue"
                        @click="() => setResource(searchValue)"
                >
                    {{ searchValue }}
                </b-dropdown-item>
            </ul>
        </div>

        <slot
                name="after"
        />
    </div>
</template>

<script>
    import AppResourceSearch from '@milestoneslabs/milestones-js-engine/src/Data/AppResourceSearch';
    import ApiFilter from '@milestoneslabs/milestones-js-engine/src/Api/ApiFilter';
    import FormErrorsMixin from '@milestoneslabs/milestones-ui-kit/src/components/FormErrors/FormErrorsMixin';
    import debounce from 'lodash/debounce';
    import {clickOutMixin} from 'bootstrap-vue/src/mixins/click-out';
    import get from 'lodash/get';
    import './PropertySearchInput.scss';
    import AddressAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/AddressAppResource";
    import ShowNotificationMixin from '@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin';
    import {store} from "../../../../../index";
    import PropertySearchRepository from "../../../../../fixtures/PropertySearchRepository";

    const PROPERTY_CONFIG = {
        apiPath: 'elasticsearch'
    };

    export default {
        name: 'PropertySearchInput',

        mixins: [
            FormErrorsMixin,
            clickOutMixin,
            ShowNotificationMixin,
        ],

        props: {
            allowUserValue: {
                type: Boolean,
                default: false,
            },
            value: {
                type: Object,
                default: () => {}
            },
        },

        data() {
            return {
                loading: false,
                collection: [],
                dropdownState: false,
                searchValue: null,
                cityAppResourceSearch: new AppResourceSearch('city'),
                rawCityAppResourceSearch: new AppResourceSearch('city'),
                zipZipInfoAppResourceSearch: new AppResourceSearch('zip-info'),
                propertyStreetAppResourceSearch: new PropertySearchRepository(PROPERTY_CONFIG),
                listenForClickOut: true,
                placeholder: 'Search...',
                activeItem: 0,
            };
        },

        watch: {
            value:{
                deep:true,
                immediate: true,
                handler(newVal) {
                    if(!Object.values(newVal).some(v => v)) {
                        this.searchValue = '';
                        this.resetData();
                    } else {
                        this.searchValue = newVal.label;
                    }
                }
            }
        },

        methods: {
            handleArrowDown() {
                if (this.activeItem < (this.collection.length - 1)) {
                    this.activeItem++;
                }
            },

            handleArrowUp() {
                if (this.activeItem > 0) {
                    this.activeItem--;
                }
            },

            handleEnter() {
                let resource = this.collection[this.activeItem];
                if (resource) {
                    this.setResource(resource);
                }
            },

            handleSearch: debounce(async function(searchValue) {
                this.activeItem = 0;
                this.searchValue = searchValue;
                let stop = !searchValue || typeof searchValue !== 'string';
                stop || (searchValue = searchValue.trim());

                if (stop || searchValue.length < 3) {
                    if(!searchValue) {
                        this.$emit('input', {value: null, label: ''});
                    }
                    this.dropdownState = false;
                    return;
                }

                this.dropdownState = true;
                await this.searchResources(searchValue);
            }, 1000),

            async searchResources(searchValue) {
                let that = this;
                that.resetData();

                await that.catchAppResourceErrors(async () => {
                    await that.generateFilters(searchValue);
                    if (!isNaN(searchValue)) {
                        await that.zipZipInfoAppResourceSearch.load();
                        that.generateCollection();
                    } else {
                        await Promise.allSettled([
                            that.rawCityAppResourceSearch.load(),
                            that.cityAppResourceSearch.load(),
                            that.propertyStreetAppResourceSearch.load(),
                        ]).then(function(results) {
                            that.generateCollection();
                            var errors = results.filter(req => req.status != 'fulfilled').length;
                            if (errors > 1) {
                                throw 'Multiple data searches failed !';
                            } else if(errors) {
                                console.error('One data search request failed !');
                            }
                        });
                    }
                }, 'loading', () => {
                    this.showNotification({
                        message: 'Failed to fetch data. Please try again later.',
                        title: 'Error',
                        type: 'error',
                    });
                });
            },

            generateCollection() {
                this.collection = [];
                this.rawCityAppResourceSearch.appResourceCollection.forEach((item) => {
                    this.collection.push({
                        name: item.name + ', ' + item.state,
                        type: 'city',
                        typeLabel: 'City',
                        value: item.name,
                        key: item.uuid,
                    });
                });
                this.cityAppResourceSearch.appResourceCollection.forEach((item) => {
                    let optionItem = {
                        name: item.name + ', ' + item.state,
                        type: 'city',
                        typeLabel: 'City',
                        value: item.name,
                        key: item.uuid,
                    };
                    if(!this.collection.some(obj => obj.key === optionItem.key)) {
                        this.collection.push(optionItem);
                    }
                });
                this.zipZipInfoAppResourceSearch.appResourceCollection.forEach((item) => {
                    this.collection.push({
                        name: item.zip,
                        type: 'zip',
                        typeLabel: 'ZIP',
                        value: item.zip,
                        key: item.uuid,
                    });
                });
                this.propertyStreetAppResourceSearch.appResourceCollection.forEach((item) => {
                    const street = get(item, 'details.address.street', null);

                    if (!street) {
                        return;
                    }

                    const optionItem = {
                        name: street,
                        type: 'street',
                        typeLabel: 'Street',
                        value: street,
                        key: item.uuid,
                    };

                    if(!this.collection.some(obj => obj.key === optionItem.key || obj.value === optionItem.value)) {
                        this.collection.push(optionItem);
                    }
                });
            },

            setPropertyResourceSearchData(searchValue) {
                this.propertyStreetAppResourceSearch.setSearchData({
                    fieldset: 'detailsAddress',
                    pageSize: 4,
                    filter: new ApiFilter({
                        params: {
                            'details.address.street': {
                                operator: 'keywords',
                                value: searchValue
                            },
                            importSource: {
                                operator: '!=',
                                value: 'system'
                            }
                        }
                    })
                });
            },

            setAppResourceSearchData(appResourceSearch, searchParam, searchValue, parsedAddressParams, relationFilters = {}) {
                let filterObject = {};
                filterObject[searchParam] = searchValue;
                let apiFilter = this.generateFilter(filterObject);
                let parsedApiFilter = this.generateFilter(parsedAddressParams);

                for (const filter in relationFilters) {
                    if (apiFilter instanceof ApiFilter) {
                        apiFilter.addRelation(filter, relationFilters[filter]);
                    }
                    if (parsedApiFilter instanceof ApiFilter) {
                        parsedApiFilter.addRelation(filter, relationFilters[filter]);
                    }
                }

                let filters = this.setFilters([apiFilter, parsedApiFilter]);
                appResourceSearch.setSearchData({filter: filters, pageSize: 4}, false);
            },

            async generateFilters(searchValue) {
                let addressAppResource = new AddressAppResource({attributes: {fullAddress: searchValue}});
                let parsedAddress = await addressAppResource.parse(addressAppResource.fullAddress);

                const attributes = {
                    street:         get(parsedAddress, 'streetName.standard'),
                    streetNumber:   get(parsedAddress, 'streetNumber.standard'),
                    state:          get(parsedAddress, 'state.standard'),
                    city:           get(parsedAddress, 'city.standard'),
                    zip:            get(parsedAddress, 'postalCode.standard'),
                    fullStreet:
                        (
                            (get(parsedAddress, 'streetNumber.standard') ?
                                (get(parsedAddress, 'streetNumber.standard') + ' '): ''
                            ) +
                            get(parsedAddress, 'streetName.standard')
                        ),
                };

                let cityParams = (({ city: name, state }) => ({ name, state }))(attributes);
                let parsedCityParams = undefined;
                if(cityParams.name) {
                    parsedCityParams = cityParams;
                }
                let parsedZipParams = (({ city, state, zip }) => ({ city, state, zip }))(attributes);
                let parsedStreetParams = (({ fullStreet: street, city, state }) => ({ street, city, state }))(attributes);
                let relationFilters = {
                    properties: new ApiFilter({
                        params: {
                            importSource: store.getters.getCurrentProjectMlsId,
                            offMarketAt: {operator: "isNull", value:true},
                        }
                    }),
                };

                this.setAppResourceSearchData(this.cityAppResourceSearch, 'name', searchValue, parsedCityParams);
                this.setAppResourceSearchData(this.zipZipInfoAppResourceSearch, 'zip', searchValue, parsedZipParams);
                this.setZipPrefParam();
                this.setRawFilters(searchValue);
                this.setPropertyResourceSearchData(searchValue);
            },

            setRawFilters(searchValue) {
                let cityFilter = new ApiFilter({
                    params: {
                        name: searchValue,
                    },
                });
                let streetFilter = new ApiFilter({
                    params: {
                        street: searchValue,
                    },
                });
                streetFilter.addRelation('properties', new ApiFilter({
                    params: {
                        importSource: store.getters.getCurrentProjectMlsId,
                        offMarketAt: {operator: "isNull", value:true},
                    }
                }));

                this.rawCityAppResourceSearch.setSearchData({filter: cityFilter, pageSize: 1});
            },

            setZipPrefParam() {
                let filters = this.zipZipInfoAppResourceSearch.filter.filters;
                filters.forEach((filter) => {
                    filter.addParam('pref', 'P');
                });
            },

            generateFilter(params) {
                if (!params) {
                    return undefined;
                }
                let filter = new ApiFilter();
                for (const [key, value] of Object.entries(params)) {
                    if(value) {
                        filter.addParam(key, value);
                    }
                }
                return filter.isValid ? filter : undefined;
            },

            setFilters(filters) {
                let apiFilter = new ApiFilter();
                apiFilter.andOr = 'or';

                filters.forEach((filter) => {
                    if(filter) {
                        apiFilter.addFilter(filter);
                    }
                });

                return apiFilter;
            },

            setResource(resource) {
                this.$emit('input', {value: this.generateAddressFilter(resource), label: String(resource.name)});
                this.dropdownState = false;
                this.searchValue = resource.name;
            },

            generateAddressFilter(resource) {
                return {
                    value: resource.value ? String(resource.value) : null,
                    nested: 'details.address.' + resource.type
                };
            },

            handleFocus() {
                if (
                    this.dropdownState ||
                    !this.searchValue
                ) {
                    return;
                }

                //since we already have a loaded results, let's show them
                if (this.collection.length) {
                    this.dropdownState = true;
                }
            },

            resetData() {
                this.collection = [];
                this.rawCityAppResourceSearch = new AppResourceSearch('city');
                this.cityAppResourceSearch = new AppResourceSearch('city');
                this.zipZipInfoAppResourceSearch = new AppResourceSearch('zip-info');
                this.propertyStreetAppResourceSearch = new PropertySearchRepository(PROPERTY_CONFIG);
            },
        },
    };
</script>
